import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api.ts';
import { COUNTRIES } from '../lib/countries.ts';
import { useAuth } from '../state/auth.tsx';
import { Alert, SelectField, Spinner, TextField } from '../components/ui.tsx';

type Errors = Partial<Record<'firstName' | 'lastName' | 'email' | 'nationality' | 'password' | 'form', string>>;

/** Mirrors the server rules so the user hears about a problem before a round trip. */
export function validate(values: {
  firstName: string; lastName: string; email: string; nationality: string; password: string;
}): Errors {
  const errors: Errors = {};
  if (!values.firstName.trim()) errors.firstName = 'Il nome è obbligatorio';
  if (!values.lastName.trim()) errors.lastName = 'Il cognome è obbligatorio';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) errors.email = 'Indirizzo email non valido';
  if (!values.nationality) errors.nationality = 'Scegli la nazionalità';
  if (values.password.length < 10) errors.password = 'Almeno 10 caratteri';
  return errors;
}

export default function Register() {
  const { register } = useAuth();
  const [values, setValues] = useState({
    firstName: '', lastName: '', email: '', nationality: 'IT', password: '', phone: '', address: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [resendState, setResend] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const set = (key: keyof typeof values) => (event: { target: { value: string } }) =>
    setValues((v) => ({ ...v, [key]: event.target.value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setBusy(true);
    try {
      await register({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        nationality: values.nationality,
        password: values.password,
        phone: values.phone.trim() || undefined,
        address: values.address.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.issues?.length) {
        const mapped: Errors = {};
        for (const issue of err.issues) {
          const key = issue.path as keyof Errors;
          mapped[key] = issue.message;
        }
        setErrors(mapped);
      } else if (err instanceof ApiError) {
        setErrors({ form: 'Registrazione non riuscita, riprova.' });
      } else {
        setErrors({ form: 'Sembra che tu sia offline. La registrazione richiede la rete.' });
      }
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setResend('sending');
    try {
      await api.resend(values.email.trim());
      setResend('sent');
    } catch {
      setResend('failed');
    }
  }

  if (done) {
    // Worded to fit both cases the server may have handled: a new account (confirmation link) and
    // an address that already had one (a note with a login and reset link). The screen must not
    // tell them apart, or it would disclose which addresses are registered.
    return (
      <div className="content narrow">
        <div className="stack center">
          <h1>Controlla la posta</h1>
          <Alert kind="info">
            Ti abbiamo scritto a <strong>{values.email.trim()}</strong>. Apri il link nell’email per
            continuare: arriva di solito entro un minuto.
          </Alert>
          {resendState === 'sent' && <Alert kind="info">Email inviata di nuovo.</Alert>}
          {resendState === 'failed' && <Alert kind="error">Non riesco a contattare il server, riprova tra poco.</Alert>}
          <p className="muted">Non è arrivata? Guarda anche nello spam.</p>
          <button className="btn secondary block" type="button" onClick={() => void resend()}
            disabled={resendState === 'sending' || resendState === 'sent'}>
            {resendState === 'sending' ? <Spinner /> : 'Rimandami l’email'}
          </button>
          <button className="btn secondary block" type="button" onClick={() => { setDone(false); setResend('idle'); }}>
            Indirizzo sbagliato? Correggilo
          </button>
          <p><Link to="/login">Torna al login</Link></p>
        </div>
      </div>
    );
  }

  return (
    <div className="content narrow">
      <div className="stack">
        <h1>Crea il tuo account</h1>
        <p className="muted">Servono solo quattro dati. Il resto è facoltativo e puoi aggiungerlo dopo.</p>

        <form className="stack" onSubmit={submit} noValidate>
          {errors.form && <Alert kind="error">{errors.form}</Alert>}
          <TextField id="firstName" label="Nome" autoComplete="given-name" required
            value={values.firstName} onChange={set('firstName')} error={errors.firstName} />
          <TextField id="lastName" label="Cognome" autoComplete="family-name" required
            value={values.lastName} onChange={set('lastName')} error={errors.lastName} />
          <TextField id="email" label="Email" type="email" inputMode="email" autoComplete="email" required
            value={values.email} onChange={set('email')} error={errors.email}
            hint="È anche il tuo nome utente." />
          <SelectField id="nationality" label="Nazionalità" required
            value={values.nationality} onChange={set('nationality')} error={errors.nationality}>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </SelectField>
          <TextField id="password" label="Password" type="password" autoComplete="new-password" required
            value={values.password} onChange={set('password')} error={errors.password}
            hint="Almeno 10 caratteri. Una frase che ricordi è meglio di simboli a caso." />
          <TextField id="phone" label="Telefono" type="tel" autoComplete="tel" optional
            value={values.phone} onChange={set('phone')} />
          <TextField id="address" label="Indirizzo" autoComplete="street-address" optional
            value={values.address} onChange={set('address')} />

          <button className="btn block" type="submit" disabled={busy}>
            {busy ? <Spinner /> : 'Registrati'}
          </button>
        </form>

        <p className="center">Hai già un account? <Link to="/login">Entra</Link></p>
      </div>
    </div>
  );
}
